// CLOUD panel — account auth (email + password) + cloud sync controls.
// Auto-sync runs every 5s in accountManager; this panel surfaces status,
// lets the player create / log into a real account (so their kingdom is
// recoverable across devices), and force a manual push/pull.

import { Events, on, emit } from './eventBus.js'
import {
  getAccountInfo, manualPush, manualPull,
  claimAccount, login, logout, loginWithGoogle, requestPasswordReset,
} from '../game/accountManager.js'
import { makeModalToggleable } from './railPanel.js'
import { pushLog } from './hud.js'

export function mountCloudPanel({ root, toggleBtn }) {
  root.innerHTML = `
    <button class="hud__cloud-close" data-cloud-close aria-label="Close cloud panel">×</button>
    <div class="hud__build-header">
      <span class="hud__build-title">SYNC TO CLOUD</span>
      <span class="hud__build-tiles" data-status>—</span>
    </div>
    <div class="hud__cloud-intro">Your kingdom auto-saves to the cloud. Sign in to keep it safe and play on any device.</div>

    <div class="hud__cloud-section" data-account-block>
      <label class="hud__cloud-label">ACCOUNT</label>
      <div class="hud__cloud-code" data-account-id>—</div>
    </div>

    <!-- Auth forms — shown only while the player is a guest (no email). -->
    <div class="hud__cloud-section" data-auth-block>
      <div class="hud__cloud-auth-tabs">
        <button data-tab="create" class="active">CREATE ACCOUNT</button>
        <button data-tab="login">LOG IN</button>
      </div>
      <input class="hud__cloud-input" type="email" data-email placeholder="email" autocomplete="email" />
      <input class="hud__cloud-input" type="password" data-password placeholder="password (min 6)" autocomplete="current-password" />
      <button class="hud__cloud-auth-submit" data-auth-submit>CREATE ACCOUNT</button>
      <button class="hud__cloud-forgot" data-forgot type="button" style="display:none">Forgot password?</button>
      <div class="hud__cloud-auth-msg" data-auth-msg></div>
      <div class="hud__cloud-or">or</div>
      <button class="hud__cloud-google" data-google type="button">Continue with Google</button>
      <div class="hud__cloud-note" data-auth-hint>
        Create an account to keep your kingdom across devices. Your current
        progress carries over.
      </div>
    </div>

    <div class="hud__cloud-section">
      <div class="hud__cloud-actions">
        <button data-push>PUSH NOW</button>
        <button data-pull>PULL FROM CLOUD</button>
      </div>
    </div>

    <div class="hud__cloud-section" data-admin-block style="display:none">
      <button class="hud__cloud-admin" data-open-admin>⚙ OPEN SUPERADMIN</button>
    </div>

    <div class="hud__cloud-section" data-logout-block style="display:none">
      <button class="hud__cloud-logout" data-logout>LOG OUT</button>
    </div>
  `

  const els = {
    status: root.querySelector('[data-status]'),
    id: root.querySelector('[data-account-id]'),
    authBlock: root.querySelector('[data-auth-block]'),
    logoutBlock: root.querySelector('[data-logout-block]'),
    adminBlock: root.querySelector('[data-admin-block]'),
    openAdmin: root.querySelector('[data-open-admin]'),
    email: root.querySelector('[data-email]'),
    password: root.querySelector('[data-password]'),
    submit: root.querySelector('[data-auth-submit]'),
    google: root.querySelector('[data-google]'),
    forgot: root.querySelector('[data-forgot]'),
    msg: root.querySelector('[data-auth-msg]'),
    hint: root.querySelector('[data-auth-hint]'),
    tabs: root.querySelectorAll('[data-tab]'),
    push: root.querySelector('[data-push]'),
    pull: root.querySelector('[data-pull]'),
    logout: root.querySelector('[data-logout]'),
  }

  let lastSyncAt = 0
  let mode = 'create' // 'create' | 'login'

  const setMode = (m) => {
    mode = m
    els.tabs.forEach((t) => t.classList.toggle('active', t.dataset.tab === m))
    els.submit.textContent = m === 'create' ? 'CREATE ACCOUNT' : 'LOG IN'
    els.hint.textContent = m === 'create'
      ? 'Create an account to keep your kingdom across devices. Your current progress carries over.'
      : 'Log into an existing account. This device will adopt that account\'s saved kingdom.'
    els.forgot.style.display = m === 'login' ? '' : 'none'
    els.msg.textContent = ''
  }
  els.tabs.forEach((t) => t.addEventListener('click', () => setMode(t.dataset.tab)))

  const refresh = () => {
    const info = getAccountInfo()
    // Status pill
    if (!info.online) {
      els.status.textContent = 'OFFLINE'
      els.status.classList.remove('active')
    } else if (lastSyncAt) {
      els.status.textContent = `synced ${new Date(lastSyncAt).toLocaleTimeString()}`
      els.status.classList.add('active')
    } else {
      els.status.textContent = 'CONNECTED'
      els.status.classList.add('active')
    }
    // Account identity line
    if (info.isRegistered) {
      els.id.textContent = info.email + (info.isAdmin ? '  ★ ADMIN' : '')
      els.authBlock.style.display = 'none'
      els.logoutBlock.style.display = ''
    } else {
      els.id.textContent = 'Guest — ' + (info.userId ?? 'no account')
      els.authBlock.style.display = info.online ? '' : 'none'
      els.logoutBlock.style.display = 'none'
    }
    // SUPERADMIN entry — only for admin accounts.
    els.adminBlock.style.display = info.isAdmin ? '' : 'none'
    els.push.disabled = !info.online
    els.pull.disabled = !info.online
  }

  els.submit.addEventListener('click', async () => {
    const email = els.email.value.trim()
    const password = els.password.value
    els.msg.textContent = '…'
    const fn = mode === 'create' ? claimAccount : login
    const res = await fn(email, password)
    if (res.ok) {
      els.msg.textContent = ''
      els.password.value = ''
      pushLog(mode === 'create'
        ? `Account created (${res.email}). Your kingdom is now saved to the cloud.`
        : `Logged in as ${res.email}.`)
      if (mode === 'login') setTimeout(() => location.reload(), 500)
      refresh()
    } else {
      els.msg.textContent = res.reason
    }
  })

  els.google.addEventListener('click', async () => {
    els.msg.textContent = 'Redirecting to Google…'
    const res = await loginWithGoogle()
    if (!res.ok) els.msg.textContent = res.reason // otherwise the browser redirects away
  })

  els.forgot.addEventListener('click', async () => {
    const email = els.email.value.trim()
    if (!email) { els.msg.textContent = 'Enter your email above first.'; return }
    els.msg.textContent = '…'
    const res = await requestPasswordReset(email)
    els.msg.textContent = res.ok
      ? 'If that email has an account, a reset link is on its way.'
      : res.reason
  })

  els.push.addEventListener('click', async () => {
    const res = await manualPush()
    pushLog(res.ok ? 'Pushed save to cloud.' : `Push failed: ${res.reason}`)
  })

  els.pull.addEventListener('click', async () => {
    if (!confirm("Replace this device's save with the cloud copy? Local changes since the last push will be lost.")) return
    const res = await manualPull()
    if (res.ok) {
      pushLog('Pulled save from cloud. Reloading…')
      setTimeout(() => location.reload(), 500)
    } else {
      pushLog(`Pull failed: ${res.reason}`)
    }
  })

  els.openAdmin.addEventListener('click', () => emit(Events.AdminOpen))

  els.logout.addEventListener('click', async () => {
    if (!confirm('Log out? This device will start a fresh guest kingdom. Your account stays safe in the cloud — log back in any time.')) return
    await logout()
    pushLog('Logged out. Reloading…')
    setTimeout(() => location.reload(), 500)
  })

  on(Events.AccountReady, refresh)
  on(Events.AccountStatePushed, ({ updatedAt }) => { lastSyncAt = updatedAt; refresh() })
  on(Events.AccountStatePulled, refresh)
  // Opened from onboarding's "Already have an account? Log in" — surface this
  // panel on the LOG IN tab so returning players aren't trapped in adoption.
  on(Events.OpenCloudLogin, () => {
    document.querySelectorAll('[data-rail-panel]').forEach((p) => p.classList.remove('open'))
    root.classList.add('open')
    setMode('login')
  })
  // Opened from the Registry header cloud icon — just reveal the panel as-is.
  on(Events.OpenCloud, () => {
    document.querySelectorAll('[data-rail-panel]').forEach((p) => p.classList.remove('open'))
    root.classList.add('open')
    refresh()
  })

  setMode('create')
  refresh()
  makeModalToggleable(root, toggleBtn)

  // Explicit close button — the panel looks like a modal (auth form), so the
  // toggle-the-rail-button-again pattern isn't discoverable. The × just
  // removes the open class, same as clicking the rail button.
  root.querySelector('[data-cloud-close]')?.addEventListener('click', () => {
    root.classList.remove('open')
  })
}
